import { AnalyticsManager } from '@descomplica/microfrontend-core';
import { container } from 'tsyringe';

import {
  MixpanelAutoplayChanged,
  MixpanelExerciseViewed,
  MixpanelLessonConsumed,
  MixpanelLessonPlayed,
  MixpanelPageview,
  MixpanelSupportMaterialOpen,
  MixpanelVideoConsumed,
} from '@/application/interfaces/classroom/tracking';

import { EssayType } from '../interfaces/pegasus.gateway';
import { isClientSide } from './generic-functions';

interface MixpanelScreenViewedEventProps {
  path: string;
  from: string;
}

interface MixpanelEvent<T> {
  eventName: string;
  props: T;
}

export interface MixpanelContentLessonCardClick {
  lesson: string;
  lesson_name: string;
  lesson_id: string;
  lesson_type: string;
  course: string;
  course_name: string;
  course_id: string;
  level_1: string;
  level_name_1: string;
  level_2?: string;
  level_name_2?: string;
  level_3?: string;
  level_name_3?: string;
  teacher?: string;
  student_id?: string;
  user?: string;
  email?: string;
  studentClassGuid?: string;
  studentClassTags?: string[] | string;
  studentClassTitle?: string;
}

export interface MixpanelEssaySent {
  essayGuid?: string;
  essayStatus?: EssayType | string;
  essayTitle: string;
  essayDescription?: string;
  essayScore?: number;
  student_id?: string;
  user?: string;
  email?: string;
  topicGuid?: string;
  topicName?: string;
  isHighlight?: boolean;
  studentClassGuid?: string;
  studentClassTags?: string[] | string;
  studentClassTitle?: string;
}

export interface MixpanelEssayCreated {
  essayGuid?: string;
  essayStatus?: EssayType | string;
  essayTitle: string;
  essayDescription?: string;
  essayScore?: number;
  student_id?: string;
  user?: string;
  email?: string;
  topicGuid?: string;
  topicName?: string;
  isHighlight?: boolean;
  studentClassGuid?: string;
  studentClassTags?: string[] | string;
  studentClassTitle?: string;
}

export interface MixpanelContentEssayCardClick {
  essayGuid?: string;
  essayStatus?: EssayType | string;
  essayTitle: string;
  essayDescription?: string;
  essayScore?: number;
  student_id?: string;
  user?: string;
  email?: string;
  topicGuid?: string;
  topicName?: string;
  studentClassGuid?: string;
  studentClassTags?: string[] | string;
  studentClassTitle?: string;
}

export interface MixpanelEssayTopic {
  topicGuid: string;
  topicName: string;
  topicImageUrl?: string;
  isHighlight?: boolean;
  student_id?: string;
  user?: string;
  email?: string;
  studentClassGuid?: string;
  studentClassTags?: string[] | string;
  studentClassTitle?: string;
}

export class Tracking {
  private static trackEvent<T>(event: T): void {
    if (!isClientSide()) return;
    const e = event as unknown as MixpanelEvent<any>;

    const analyticsManager = container.resolve(AnalyticsManager);

    analyticsManager.trackEvent(e.eventName, {
      ...e.props,
    });
  }

  public static pageView(pageView: MixpanelPageview): void {
    if (!isClientSide()) return;
    this.trackEvent<MixpanelEvent<MixpanelScreenViewedEventProps>>({
      eventName: 'Page View',
      props: {
        ...pageView,
        path: location.pathname,
        from: 'descomplica-io-lms',
      },
    });
  }

  public static contentLessonCardClick(
    contentLessonCardClickEvent: MixpanelContentLessonCardClick,
  ): void {
    if (!isClientSide()) return;
    this.trackEvent<MixpanelEvent<MixpanelContentLessonCardClick>>({
      eventName: 'ContentLessonCard Clicked',
      props: contentLessonCardClickEvent,
    });
  }

  public static topicEssayCardClick(topicEssayCardClickEvent: MixpanelEssayTopic): void {
    if (!isClientSide()) return;
    this.trackEvent<MixpanelEvent<MixpanelEssayTopic>>({
      eventName: 'Essay topic Clicked',
      props: topicEssayCardClickEvent,
    });
  }

  public static essaySent(essaySentEvent: MixpanelEssaySent) {
    if (!isClientSide()) return;
    this.trackEvent<MixpanelEvent<MixpanelEssaySent>>({
      eventName: 'Essay sent',
      props: essaySentEvent,
    });
  }

  public static essayCreated(essayCreated: MixpanelEssayCreated) {
    if (!isClientSide()) return;
    this.trackEvent<MixpanelEvent<MixpanelEssayCreated>>({
      eventName: 'Essay created',
      props: essayCreated,
    });
  }

  public static contentEssayCardClick(contentEssayCardClickEvent: MixpanelContentEssayCardClick) {
    if (!isClientSide()) return;
    this.trackEvent<MixpanelEvent<MixpanelContentEssayCardClick>>({
      eventName: 'ContentEssayCard Clicked',
      props: contentEssayCardClickEvent,
    });
  }

  public static supportMaterialOpened(suportMaterialEvent: MixpanelSupportMaterialOpen): void {
    if (!isClientSide()) return;
    this.trackEvent<MixpanelEvent<MixpanelSupportMaterialOpen>>({
      eventName: 'Support Material Opened',
      props: suportMaterialEvent,
    });
  }

  public static exerciseViewed(exerciseViewedEvent: MixpanelExerciseViewed): void {
    if (!isClientSide()) return;
    this.trackEvent<MixpanelEvent<MixpanelExerciseViewed>>({
      eventName: 'Exercise Viewed',
      props: exerciseViewedEvent,
    });
  }

  public static lessonPlayed(lessonPlayedEvent: MixpanelLessonPlayed): void {
    if (!isClientSide()) return;
    this.trackEvent<MixpanelEvent<MixpanelLessonPlayed>>({
      eventName: 'Lesson Played',
      props: lessonPlayedEvent,
    });
  }

  public static autoplayChanged(autoplayChangedEvent: MixpanelAutoplayChanged): void {
    if (!isClientSide()) return;
    this.trackEvent<MixpanelEvent<MixpanelAutoplayChanged>>({
      eventName: 'Autoplay changed',
      props: autoplayChangedEvent,
    });
  }

  public static lessonConsumed(lessonConsumedEvent: MixpanelLessonConsumed): void {
    if (!isClientSide()) return;
    this.trackEvent<MixpanelEvent<MixpanelLessonConsumed>>({
      eventName: 'Lesson Consumed',
      props: lessonConsumedEvent,
    });
  }

  public static videoConsumed(videoConsumedEvent: MixpanelVideoConsumed): void {
    if (!isClientSide()) return;
    this.trackEvent<MixpanelEvent<MixpanelVideoConsumed>>({
      eventName: 'Video Consumed',
      props: videoConsumedEvent,
    });
  }
}
